import { brand } from "@scryerlabs/branding";
import type { TrackerStrategyResultDto } from "../../api/hooks/usePortfolioTracker";

export const TRADES_PER_PAGE = 10;

const MINUS = "−";
const DAY_MS = 864e5;

export interface DatedValue {
  date: string;
  value: number;
}

export const formatMoney = (value: number): string => {
  const rounded = Math.round(value);
  return `${rounded < 0 ? MINUS : ""}$${Math.abs(rounded).toLocaleString("en-US")}`;
};

export const formatSignedMoney = (value: number): string => {
  const rounded = Math.round(value);
  const sign = rounded > 0 ? "+" : rounded < 0 ? MINUS : "";
  return `${sign}$${Math.abs(rounded).toLocaleString("en-US")}`;
};

/** "20%", "33.3%". */
export const formatPercent = (value: number): string =>
  `${(Math.round(value * 10) / 10).toLocaleString("en-US")}%`;

/** "+12.4%", "−3.0%". */
export const formatSignedPercent = (value: number): string => {
  const rounded = Math.round(value * 10) / 10;
  const sign = rounded > 0 ? "+" : rounded < 0 ? MINUS : "";
  return `${sign}${Math.abs(rounded).toFixed(1)}%`;
};

/** A fall from a high, given as a positive percent: "−4.3%". */
export const formatFall = (percent: number): string =>
  percent >= 0.05 ? `${MINUS}${formatPercent(percent)}` : "0%";

export const formatPrice = (value: number): string =>
  `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: value < 1 ? 4 : 2 })}`;

/** Green from half a dollar up, red from half a dollar down, as the figures round. */
export const toneOf = (value: number): string =>
  value >= 0.5 ? "text-success" : value <= -0.5 ? "text-danger" : "";

// The API's dates are calendar days, so they are read and shown in UTC: a viewer west of Greenwich would
// otherwise see each one as the day before.
const DAY: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" };

/** "Sep 2, 2025". */
export const formatDay = (day: string): string => new Date(Date.parse(day)).toLocaleDateString("en-US", DAY);

/** "May 12 – 27, 2026". */
export const formatDayRange = (from: string, to: string): string =>
  new Intl.DateTimeFormat("en-US", DAY).formatRange(new Date(Date.parse(from)), new Date(Date.parse(to)));

export const daysBetween = (from: string, to: string): number =>
  Math.round((Date.parse(to) - Date.parse(from)) / DAY_MS);

/** Shares in tenths of a percent that add up to exactly 100; the spare tenths go to the largest remainders. */
export function sharesOf(weights: number[]): number[] {
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  if (!weights.length || total <= 0) return weights.map(() => 0);

  const raw = weights.map((weight) => (weight / total) * 1000);
  const base = raw.map(Math.floor);
  let spare = 1000 - base.reduce((sum, tenths) => sum + tenths, 0);
  raw
    .map((value, index) => [value - base[index], index] as const)
    .sort((a, b) => b[0] - a[0])
    .forEach(([, index]) => {
      if (spare > 0) {
        base[index]++;
        spare--;
      }
    });
  return base.map((tenths) => tenths / 10);
}

const RAMPS = {
  light: [brand.navy, brand.blue, brand.lightBlue, "#9BE0FF"],
  // Starts lighter, so the first band still shows on a dark card.
  dark: ["#2E44A8", brand.blue, brand.lightBlue, "#E3F8FF"],
} as const;

type Lab = [number, number, number];

function toLab(hex: string): Lab {
  const linear = (channel: number) => {
    const c = channel / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const [r, g, b] = [1, 3, 5].map((at) => linear(parseInt(hex.slice(at, at + 2), 16)));
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}

function toHex([L, a, b]: Lab): string {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  const gamma = (c: number) =>
    Math.round(255 * Math.min(1, Math.max(0, c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055)));
  return `#${[
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ]
    .map((channel) => gamma(channel).toString(16).padStart(2, "0"))
    .join("")}`;
}

/**
 * One colour per strategy: even steps along the brand gradient, Navy to Blue to Light Blue, spread over
 * however many the portfolio holds. Measured in OKLab so the steps look even.
 */
export function strategyColours(count: number, theme: "light" | "dark"): string[] {
  if (count <= 1) return [brand.blue];

  const labs = RAMPS[theme].map(toLab);
  const lengths = labs.slice(1).map((point, index) => Math.hypot(...point.map((value, k) => value - labs[index][k])));
  const total = lengths.reduce((sum, length) => sum + length, 0);

  return Array.from({ length: count }, (_, index) => {
    let along = (total * index) / (count - 1);
    let segment = 0;
    while (segment < lengths.length - 1 && along > lengths[segment]) {
      along -= lengths[segment];
      segment++;
    }
    const t = lengths[segment] ? along / lengths[segment] : 0;
    const from = labs[segment];
    const to = labs[segment + 1];
    return toHex([0, 1, 2].map((k) => from[k] + (to[k] - from[k]) * t) as Lab);
  });
}

/** What a strategy had made by the end of `day`, from the trades that had closed by then. */
export function profitTo(strategy: TrackerStrategyResultDto, day: string): number {
  return strategy.trades.reduce(
    (sum, trade) => sum + (trade.profit !== null && trade.exitDate <= day ? trade.profit : 0),
    0,
  );
}

/** A strategy's profit so far: nothing at its start, a step at each exit, then flat to today. */
export function profitSeries(strategy: TrackerStrategyResultDto, asOf: string): DatedValue[] {
  const points: DatedValue[] = [{ date: strategy.start, value: 0 }];
  let running = 0;
  [...strategy.trades]
    .filter((trade) => trade.profit !== null)
    .sort((a, b) => a.exitDate.localeCompare(b.exitDate))
    .forEach((trade) => {
      running += trade.profit!;
      points.push({ date: trade.exitDate, value: running });
    });
  if (points[points.length - 1].date < asOf) points.push({ date: asOf, value: running });
  return points;
}

/** Each strategy's profit piled up from what was put in, gains above it and losses hanging below. */
export interface ProfitStack {
  dates: string[];
  bands: { id: string; gains: number[][]; losses: number[][] }[];
  /** The highest the chart has to reach. */
  top: number;
}

/** Stacks each strategy's profit at every day the balance moves, in the setup's order. */
export function stackProfits(
  dates: string[],
  putIn: number,
  balance: DatedValue[],
  profits: { id: string; values: number[] }[],
): ProfitStack {
  let up = dates.map(() => putIn);
  let down = up;
  const bands: ProfitStack["bands"] = [];
  for (const { id, values } of profits) {
    const gains = [up, up.map((value, index) => value + Math.max(0, values[index]))];
    const losses = [down, down.map((value, index) => value + Math.min(0, values[index]))];
    up = gains[1];
    down = losses[1];
    bands.push({ id, gains, losses });
  }
  return { dates, bands, top: Math.max(...up, ...balance.map((point) => point.value), putIn) };
}

/** The value a stepped series holds on `day`. */
export function valueAt(points: DatedValue[], day: string): number {
  let value = points[0]?.value ?? 0;
  for (const point of points) {
    if (point.date > day) break;
    value = point.value;
  }
  return value;
}

/** What Profit by strategy opens with, as an i18n key under results.takeaway and what it names. */
export type Takeaway =
  | { key: "nothingClosed" }
  | { key: "noneUp" }
  | { key: "all"; name: string }
  | { key: "spread"; name: string; percent: number }
  | { key: "most"; names: string[]; percent: number };

export function takeawayOf(items: { name: string; profit: number }[]): { lead: Takeaway; down: string[] } {
  const gains = items.filter((item) => item.profit >= 0.5).sort((a, b) => b.profit - a.profit);
  const down = items.filter((item) => item.profit <= -0.5).map((item) => item.name);
  const sumGains = gains.reduce((sum, item) => sum + item.profit, 0);

  if (!gains.length) return { lead: down.length ? { key: "noneUp" } : { key: "nothingClosed" }, down };
  if (gains.length === 1) return { lead: { key: "all", name: gains[0].name }, down };
  if (gains[0].profit / sumGains < 0.35) {
    return { lead: { key: "spread", name: gains[0].name, percent: (gains[0].profit / sumGains) * 100 }, down };
  }

  let count = 1;
  let part = gains[0].profit;
  while (part < sumGains * 0.6 && count < Math.min(3, gains.length)) part += gains[count++].profit;
  return {
    lead: { key: "most", names: gains.slice(0, count).map((item) => item.name), percent: (part / sumGains) * 100 },
    down,
  };
}

export const CHART_PAD = { left: 58, right: 14, top: 16, bottom: 26 };

export function niceStep(raw: number): number {
  const power = 10 ** Math.floor(Math.log10(raw));
  const ratio = raw / power;
  return (ratio <= 1 ? 1 : ratio <= 2 ? 2 : ratio <= 2.5 ? 2.5 : ratio <= 5 ? 5 : 10) * power;
}

/** "$120k", "−$2.5k", "$1.25m". */
export function axisMoney(value: number): string {
  const size = Math.abs(value);
  const sign = value < 0 ? MINUS : "";
  if (size >= 1e6) return `${sign}$${+(size / 1e6).toFixed(2)}m`;
  if (size >= 1e3) return `${sign}$${+(size / 1e3).toFixed(1)}k`;
  return `${sign}$${Math.round(size)}`;
}

export interface ChartFrame {
  width: number;
  height: number;
  left: number;
  right: number;
  y: (value: number) => number;
  x: (day: string) => number;
  /** The day under a point across the plot, kept within the chart's days. */
  dayAt: (px: number) => string;
  gridlines: { value: number; y: number; zero: boolean }[];
}

/** Round-number gridlines over `low` to `high`, and days across from `firstDay` to `lastDay`. */
export function chartFrame(
  width: number,
  height: number,
  low: number,
  high: number,
  firstDay: string,
  lastDay: string,
): ChartFrame {
  const step = niceStep((high - low) / 4 || 1);
  const lo = Math.floor(low / step) * step;
  let hi = Math.ceil(high / step) * step;
  if (hi === lo) hi = lo + step;

  const y = (value: number) =>
    CHART_PAD.top + ((hi - value) / (hi - lo)) * (height - CHART_PAD.top - CHART_PAD.bottom);
  const left = CHART_PAD.left;
  const right = width - CHART_PAD.right;
  const t0 = Date.parse(firstDay);
  const span = Math.max(DAY_MS, Date.parse(lastDay) - t0);
  const x = (day: string) => left + ((Date.parse(day) - t0) / span) * (right - left);
  const dayAt = (px: number) => {
    const clamped = Math.min(right, Math.max(left, px));
    const day = new Date(t0 + ((clamped - left) / (right - left)) * span).toISOString().slice(0, 10);
    return day < firstDay ? firstDay : day > lastDay ? lastDay : day;
  };

  const gridlines: ChartFrame["gridlines"] = [];
  for (let value = lo; value <= hi + step / 2; value += step) {
    const zero = Math.abs(value) < step / 2;
    gridlines.push({ value: zero ? 0 : value, y: y(value), zero: zero && lo < 0 });
  }

  return { width, height, left, right, y, x, dayAt, gridlines };
}

const MONTH: Intl.DateTimeFormatOptions = { month: "short", timeZone: "UTC" };
const MONTH_YEAR: Intl.DateTimeFormatOptions = { month: "short", year: "numeric", timeZone: "UTC" };
const DAY_MONTH: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", timeZone: "UTC" };

/** Labels along the bottom: a week apart over a short stretch, otherwise months, at most `most` of them. */
export function timeTicks(from: string, to: string, most = 8): { day: string; label: string }[] {
  const ticks: { day: string; label: string }[] = [];
  if (daysBetween(from, to) < 50) {
    for (let at = Date.parse(from) + 7 * DAY_MS; at < Date.parse(to); at += 7 * DAY_MS) {
      ticks.push({ day: new Date(at).toISOString().slice(0, 10), label: new Date(at).toLocaleDateString("en-US", DAY_MONTH) });
    }
    const everyWeeks = Math.ceil(ticks.length / most);
    return ticks.filter((_, index) => index % everyWeeks === 0);
  }

  let [year, month] = from.split("-").map(Number);
  for (;;) {
    month++;
    if (month > 12) {
      month = 1;
      year++;
    }
    const day = `${year}-${String(month).padStart(2, "0")}-01`;
    if (day > to) break;
    const date = new Date(Date.parse(day));
    ticks.push({ day, label: date.toLocaleDateString("en-US", month === 1 ? MONTH_YEAR : MONTH) });
  }
  const every = Math.ceil(ticks.length / most);
  return ticks.filter((_, index) => index % every === 0);
}

/** A stepped line through the points: flat until each point, then straight up or down to it. */
export function stepPath(points: { x: number; y: number }[]): string {
  if (!points.length) return "";
  let path = `M${points[0].x.toFixed(1)},${points[0].y.toFixed(1)}`;
  for (let index = 1; index < points.length; index++) {
    path += `H${points[index].x.toFixed(1)}V${points[index].y.toFixed(1)}`;
  }
  return path;
}

/** Room above and below the values, so a flat line still has an axis to sit on. */
export function lineBounds(points: DatedValue[], base: number): [number, number] {
  const values = points.map((point) => point.value).concat(base);
  const low = Math.min(...values);
  const high = Math.max(...values);
  const room = Math.max((high - low) * 0.08, Math.abs(base) * 0.01, 1);
  return [low - room, high + room];
}
